// A step grid over a MIDI clip — the drum-machine view of the same notes
// the piano roll edits. Rows are pitches (General MIDI's percussion names
// for a kit, plus whatever else the clip holds); columns are steps of a
// grid over the clip's length; a cell is on when a note of that pitch
// STARTS inside the step. Toggling a cell adds a one-step note or removes
// the notes starting there. Nothing here is a new wire shape: a toggle is
// an ordinary note edit, synced like any other.

import type { WireNote } from "@shared/midi/notes.js";

export interface StepRow {
  pitch: number;
  label: string;
  /** Against the scale the rows follow, when they do. */
  tone?: Tone;
}

/** General MIDI's percussion map, as far as the preview kit plays it
 * (lib/drums.ts): the name a drum row carries wherever a pitch is shown —
 * the step grid's rows, the roll's keys. Live labels a Drum Rack's keys the
 * same way; FL gives each drum its own roll. */
const DRUM_NAMES: Readonly<Record<number, string>> = {
  35: "Kick 2",
  36: "Kick",
  37: "Sidestick",
  38: "Snare",
  39: "Clap",
  40: "Snare 2",
  41: "Low tom",
  42: "Closed hat",
  43: "Floor tom",
  44: "Pedal hat",
  45: "Mid tom",
  46: "Open hat",
  47: "Mid tom 2",
  48: "High tom",
  49: "Crash",
  50: "High tom 2",
  51: "Ride",
  52: "China",
  53: "Ride bell",
  54: "Tambourine",
  55: "Splash",
  56: "Cowbell",
  57: "Crash 2",
  59: "Ride 2",
};

/** A kit's own names for its pads, by pitch — what a row says when the
 * kit is known to hold something other than General MIDI's drum there (a
 * Drum Rack's pads, once the room carries them: see `drumLabel`). */
export type DrumNames = Readonly<Record<number, string>>;

/** The drum a kit plays at `pitch`: the kit's own name for the pad when it
 * has one, else General MIDI's — which IS the browser's built-in kit (its
 * voices, lib/drums.ts `kitVoice`, follow the same map) — else null. */
export function drumLabel(pitch: number, names?: DrumNames): string | null {
  const p = Math.round(pitch);
  const own = names?.[p]?.trim();
  return own ? own : (DRUM_NAMES[p] ?? null);
}

/** The kit rows, low to high: the pads a beat is usually programmed on. */
export const DRUM_ROWS: readonly StepRow[] = [36, 37, 38, 39, 41, 42, 45, 46, 48, 49, 51, 56].map((pitch) => ({ pitch, label: DRUM_NAMES[pitch]! }));

const NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
export function pitchLabel(pitch: number): string {
  return `${NAMES[((pitch % 12) + 12) % 12]}${Math.floor(pitch / 12) - 2}`;
}

/** How many steps of `stepBeats` cover a clip of `clipBeats` (the last may be partial). */
export function stepCount(clipBeats: number, stepBeats: number): number {
  if (!(stepBeats > 0) || !(clipBeats > 0)) return 0;
  return Math.ceil(clipBeats / stepBeats - 1e-9);
}

/** The step a note's start falls in. */
export function stepOf(startTime: number, stepBeats: number): number {
  return Math.floor(startTime / stepBeats + 1e-9);
}

/** A scale the rows can follow — the roll's (the Set's while Live's Scale
 * Mode is on, else the browser's drawing aid). Intervals: semitones above root. */
export interface RowScale {
  root: number;
  intervals: readonly number[];
}

/** Where a pitch sits against the scale: the root, another degree, or outside. */
export type Tone = "root" | "in" | "out";
export function toneOf(pitch: number, scale: RowScale): Tone {
  const degree = (((pitch - scale.root) % 12) + 12) % 12;
  return degree === 0 ? "root" : scale.intervals.includes(degree) ? "in" : "out";
}

/** The next pitch to open a row for, one step above (`dir` 1) or below (-1)
 * `pitch`: a semitone, or with a scale the next degree. Null past the ends. */
export function neighbourPitch(pitch: number, dir: 1 | -1, scale?: RowScale): number | null {
  for (let p = pitch + dir; p >= 0 && p <= 127; p += dir) {
    if (!scale || toneOf(p, scale) !== "out") return p;
  }
  return null;
}

/** The rows to show, HIGHEST FIRST (a piano roll's way up — and a Drum
 * Rack's, kick at the bottom): the kit's (when `kit`) or the clip's own
 * pitches, plus any other pitch the clip holds — a note never hides behind
 * the view. With a scale, a melodic clip's rows are the scale's degrees from
 * its lowest note to its highest (an empty one: an octave up from the root
 * nearest middle C), its out-of-scale notes still shown, marked. */
export function rowsFor(notes: readonly WireNote[], kit: boolean, scale?: RowScale, names?: DrumNames): StepRow[] {
  // A kit that names its pads shows those pads (Live's Drum Rack shows its
  // filled ones); otherwise the pads a beat is usually programmed on.
  const named: StepRow[] = Object.entries(names ?? {})
    .map(([p, label]) => ({ pitch: Number(p), label: label.trim() }))
    .filter((r) => Number.isInteger(r.pitch) && r.pitch >= 0 && r.pitch <= 127 && r.label !== "");
  const rows: StepRow[] = !kit ? [] : named.length > 0 ? named : DRUM_ROWS.map((r) => ({ ...r }));
  if (!kit) {
    if (scale) {
      // Degrees across the clip's range, or an octave to click.
      let lo: number, hi: number;
      if (notes.length === 0) {
        lo = 60 + ((scale.root - 60) % 12 + 12) % 12; // the root at or above C3
        hi = lo + 12;
      } else {
        lo = Math.min(...notes.map((n) => n.pitch));
        hi = Math.max(...notes.map((n) => n.pitch));
      }
      for (let p = Math.max(0, lo); p <= Math.min(127, hi); p++) if (toneOf(p, scale) !== "out") rows.push({ pitch: p, label: pitchLabel(p) });
    } else if (notes.length === 0) {
      // A melodic clip shows its own pitches; an EMPTY one an octave around
      // middle C, so there is something to click.
      for (let i = 0; i < 13; i++) rows.push({ pitch: 60 + i, label: pitchLabel(60 + i) });
    }
  }
  const known = new Set(rows.map((r) => r.pitch));
  for (const n of notes) {
    if (known.has(n.pitch)) continue;
    known.add(n.pitch);
    rows.push({ pitch: n.pitch, label: (kit ? drumLabel(n.pitch, names) : null) ?? pitchLabel(n.pitch) });
  }
  // A kit's rows are drums, not degrees: no tones, so nothing dims or folds.
  if (scale && !kit) for (const r of rows) r.tone = toneOf(r.pitch, scale);
  return rows.sort((a, b) => b.pitch - a.pitch);
}

/** Each step's state for one row: on when a note of the pitch starts in it,
 * carrying the loudest such note's velocity (an accent reads as one). */
export function stepsOf(notes: readonly WireNote[], pitch: number, stepBeats: number, count: number): Array<{ on: boolean; velocity: number }> {
  const out = Array.from({ length: count }, () => ({ on: false, velocity: 0 }));
  for (const n of notes) {
    if (n.pitch !== pitch || n.muted) continue;
    const s = stepOf(n.startTime, stepBeats);
    const cell = out[s];
    if (!cell) continue;
    cell.on = true;
    cell.velocity = Math.max(cell.velocity, n.velocity);
  }
  return out;
}

/** Toggle one cell: notes of the pitch starting in the step are removed;
 * an empty cell gains a note one step long at `velocity`. */
export function toggleStep(notes: readonly WireNote[], pitch: number, step: number, stepBeats: number, velocity = 100): WireNote[] {
  // A muted note is not a hit (the cell reads off) and is left alone either way.
  const inStep = (n: WireNote): boolean => !n.muted && n.pitch === pitch && stepOf(n.startTime, stepBeats) === step;
  if (notes.some(inStep)) return notes.filter((n) => !inStep(n));
  return [...notes, { pitch, startTime: step * stepBeats, duration: stepBeats, velocity }];
}

/** Set one cell on or off, idempotently: a sweep re-entering a cell must
 * not flip it back. On an already-on cell `on` leaves the notes alone (their
 * velocity included); off an already-off cell returns `notes` itself. */
export function setStep(notes: readonly WireNote[], pitch: number, step: number, stepBeats: number, on: boolean, velocity = 100): readonly WireNote[] {
  const inStep = (n: WireNote): boolean => !n.muted && n.pitch === pitch && stepOf(n.startTime, stepBeats) === step;
  const lit = notes.some(inStep);
  if (on === lit) return notes;
  return on ? [...notes, { pitch, startTime: step * stepBeats, duration: stepBeats, velocity }] : notes.filter((n) => !inStep(n));
}

/** A sweep: every cell crossed set to `on` — painting a run of hits, or
 * erasing one, across rows too. One edit, however many cells. */
export function paintSteps(notes: readonly WireNote[], cells: ReadonlyArray<{ pitch: number; step: number }>, stepBeats: number, on: boolean, velocity = 100): readonly WireNote[] {
  let out = notes;
  for (const c of cells) out = setStep(out, c.pitch, c.step, stepBeats, on, velocity);
  return out;
}

/** Which beat a step sits in — alternate beats shade differently, so 16ths
 * read in fours (a drum machine's 1 · 2 · 3 · 4). */
export function beatGroup(step: number, stepBeats: number): number {
  return Math.floor(step * stepBeats + 1e-9);
}

/** Set every sounding note starting in the cell to `velocity` — an accent, or a ghost. */
export function setStepVelocity(notes: readonly WireNote[], pitch: number, step: number, stepBeats: number, velocity: number): WireNote[] {
  const v = Math.max(1, Math.min(127, Math.round(velocity)));
  return notes.map((n) => (!n.muted && n.pitch === pitch && stepOf(n.startTime, stepBeats) === step ? { ...n, velocity: v } : n));
}

/** The velocity a repeated tap cycles to: normal → accent → ghost → normal. */
export function nextVelocity(velocity: number): number {
  if (velocity >= 120) return 48;
  if (velocity <= 64) return 100;
  return 127;
}

/** A cell of the grid: a row's pitch and a step. */
export interface StepCell {
  pitch: number;
  step: number;
}

/** Move a hit — the piano roll's drag, on the grid: every sounding note
 * starting in `from` shifts by whole steps to `to`'s step and takes `to`'s
 * pitch, keeping its length, velocity and any offset inside the step. The
 * hit it lands on is replaced (a drum machine's cell holds one hit, and
 * Live's roll trims what a moved note lands over). Muted notes stay where
 * they are, as everywhere else on the grid. The same cell, or an empty
 * `from`, returns `notes` itself. */
export function moveStep(notes: readonly WireNote[], from: StepCell, to: StepCell, stepBeats: number): readonly WireNote[] {
  if (from.pitch === to.pitch && from.step === to.step) return notes;
  const inCell = (n: WireNote, c: StepCell): boolean => !n.muted && n.pitch === c.pitch && stepOf(n.startTime, stepBeats) === c.step;
  if (!notes.some((n) => inCell(n, from))) return notes;
  const shift = (to.step - from.step) * stepBeats;
  const out: WireNote[] = [];
  for (const n of notes) {
    if (inCell(n, from)) out.push({ ...n, pitch: to.pitch, startTime: Math.max(0, n.startTime + shift) });
    else if (!inCell(n, to)) out.push(n);
  }
  return out;
}

/** Where an arrow key takes a cell: `dStep` steps along (clamped to the
 * grid's `count`), `dRow` rows down the list (`rows` highest first, as
 * drawn, so +1 is the row below). Null when it cannot move. */
export function nudgeCell(rows: readonly number[], count: number, cell: StepCell, dRow: number, dStep: number): StepCell | null {
  const at = rows.indexOf(cell.pitch);
  if (at < 0 || count <= 0) return null;
  const row = Math.max(0, Math.min(rows.length - 1, at + dRow));
  const step = Math.max(0, Math.min(count - 1, cell.step + dStep));
  const pitch = rows[row]!;
  return pitch === cell.pitch && step === cell.step ? null : { pitch, step };
}
